<template>
  <div class="fts-form-footer-buttons" ref="form-footer-buttons">
    <div 
      class="form-footer-button-container" 
      :class="{'form-footer-button-container--expand': isExpand}" 
      ref="button-container"
    >
      <fts-btn
        v-for="action in activities"
        :key="action.id"
        outline
        :color="action.color || 'primary'"
        :emoji="action.emoji"
        text-transform-init
        class="btn-activity form-footer-button"
        :class="{'form-footer-button--design': designMode}"
        @click="$emit('execute:activity', action)"
      >
        <span class="btn-activity-label">{{action.name}}
          <fts-test-badge v-if="action.development" :stage="action.development"></fts-test-badge>
        </span>

        <fts-tippy 
          content="Edit action" 
          append-trigger-class="form-footer-design-btn" 
          :stop-propagation="true" 
          :prevent-default="true" 
          class="form-footer-design-wrapper" 
          @trigger:click="openDesignWorkflowDrawer({ tab: 'action', action: 'edit', data: action })" 
          v-if="designMode"
        >
          <span class="material-icons-outlined">more_vert</span>
        </fts-tippy>
      </fts-btn>

      <fts-tippy 
        append-trigger-class="form-toggle-more-btn" 
        content="Create new action" 
        :options="{ placement: 'right' }" 
        @trigger:click="openDesignWorkflowDrawer({ tab: 'action', action: 'create' })" 
        v-if="designMode"
      >
        <span class="material-icons-outlined form-toggle-more-btn__icon">add</span>
      </fts-tippy>

      <fts-btn
        v-for="action in documents"
        :key="action.id"
        outline
        color="ternary-green"
        text-transform-init
        class="btn-activity"
        @click="$emit('execute:document', action)"
      >
        <font-awesome-icon :icon="['far', 'file-pdf']" class="fts-file-pdf" v-if="action.typeId === 'pdf'" />
        <font-awesome-icon :icon="['far', 'file-alt']" class="fts-file-excel" v-else-if="action.typeId === 'spreadsheet'" />
        <font-awesome-icon :icon="['far', 'file-code']" class="fts-file-html" v-else-if="action.typeId === 'html'" />
        <font-awesome-icon :icon="['far', 'file']" class="fts-file-default" v-else />
        <span class="btn-activity-label">{{action.name}}
          <fts-test-badge v-if="action.development" :stage="action.development"></fts-test-badge>
        </span>
      </fts-btn>

      <fts-btn
        v-for="action in customs"
        :key="action.id"
        outline
        :color="action.color || 'ternary-blue'"
        :emoji="action.emoji"
        text-transform-init
        class="btn-activity"
        @click="$emit('execute:custom', action)"
      >
        <span class="btn-activity-label">{{action.name}}</span>
      </fts-btn>

      <div 
        class="form-toggle-more-btn" 
        @click="toggleExpand(false)" 
        v-if="isExpand && hiddenButtonCount > 0"
      >See less</div>
    </div>

    <div 
      class="form-footer-toggle-container" 
      ref="toggle-container"
      v-if="!isExpand && hiddenButtonCount > 0"
    >
      <div 
        class="form-toggle-more-btn" 
        @click="toggleExpand(true)"
        ref="toggle-btn"
      >
        <span class="material-icons-outlined form-toggle-more-btn__icon">more_horiz</span>
        <span>+{{hiddenButtonCount}} more</span>
      </div>
    </div>
  </div>
</template>

<script>
import FTSBtn from '../../elements/FTSBtn';
import FTSTestBadge from '../../elements/FTSTestBadge';
import FTSTippy from '../../elements/FTSTippy';

export default {
  props: {
    activities: Array,
    customs: Array,
    documents: Array,
    designMode: Boolean
  },
  components: {
    "fts-btn": FTSBtn,
    "fts-test-badge": FTSTestBadge,
    "fts-tippy": FTSTippy
  },
  data: () => ({
    resizeHandler: null,
    resizeObserver: null,
    isExpand: false,
    hiddenButtonCount: 0
  }),
  methods: {
    calculate() {
      const OFFSET_MARGIN = 6;
      const btnContainerEl = this.$refs["button-container"];
      const btnContainerElChildren = btnContainerEl.children;

      let totalButtons = btnContainerElChildren.length || 0;
      let availableWidth = btnContainerEl.getBoundingClientRect().width;
      let fitButtons = 0;

      for (let i = 0; i < totalButtons; i++) {
        const btnEl = btnContainerElChildren[i];
        const btnWidth = btnEl.getBoundingClientRect().width + OFFSET_MARGIN;

        if (btnWidth > availableWidth) break;

        availableWidth = availableWidth - btnWidth;
        fitButtons += 1;
      }

      return {
        availableWidth: availableWidth,
        hiddenButtonCount: totalButtons - fitButtons,
        visibleButtonCount: fitButtons
      }
    },
    async calculateLayout() {
      this.hiddenButtonCount = 0;
      await this.$nextTick();

      let result = this.calculate();

      if (result.hiddenButtonCount > 0) {
        this.hiddenButtonCount = result.hiddenButtonCount;
        await this.$nextTick();

        result = this.calculate();
        this.hiddenButtonCount = result.hiddenButtonCount;
      }

      if (result.hiddenButtonCount) {
        const toggleBtnEl = this.$refs["toggle-btn"];

        if (toggleBtnEl) {
          const transformX = result.availableWidth - 6;
          if (transformX > 0) {
            toggleBtnEl.style.transform = `translateX(-${transformX}px)`;
          }
        }
      }
    },
    toggleExpand(value) {
      this.isExpand = value;
    },
    startResizeObserver() {
      if (this.resizeObserver) return;

      this.resizeHandler = this.calculateLayout;

      const el = this.$refs["form-footer-buttons"];
      if (!el) return;
      this.resizeObserver = new ResizeObserver(this.resizeHandler);
      this.resizeObserver.observe(el);
    },
    stopResizeObserver() {
      if (!this.resizeObserver) return;

      const btnContainerEl = this.$refs["button-container"];
      btnContainerEl && this.resizeObserver.unobserve(btnContainerEl);

      this.resizeObserver.disconnect();
      this.resizeObserver = null;

      if (this.resizeHandler) {
        this.resizeHandler = null;
      }
    },
    openDesignWorkflowDrawer(args) {
      window.eventBus.$emit("lite:openDesignWorkflowDrawer", args);
    }
  },
  watch: {
    designMode() {
      this.$nextTick(() => {
        this.calculateLayout();
      });
    }
  },
  mounted() {
    this.startResizeObserver();
  },
  beforeDestroy() {
    this.stopResizeObserver();
  }
}
</script>

<style lang="scss">
@use "./src/assets/scss/vars.scss" as *;

.fts-form-footer-buttons {
  display: flex;
  background-color: var(--inistate-color-shade-white);
  .form-footer-button-container {
    @include flex-gap($gap: 6px);
    max-height: 42px;
    overflow: hidden;
    &.form-footer-button-container--expand {
      max-height: unset;
    }
    > .fts-btn {
      position: relative;
    }
  }
  .form-footer-toggle-container {
    flex-shrink: 0;
  }
  .form-footer-button {
    &.form-footer-button--design {
      &:hover {
        .form-footer-design-wrapper {
          display: flex;
          align-items: center;
        }
      }
    }
  }
  .form-footer-design-wrapper {
    @include absolute-center-y;
    right: 4px;
    display: none;
    padding: 2px;
    border-radius: var(--inistate-rounded-4);
    background-color: var(--inistate-color-shade-white);
  }
  .form-footer-design-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 24px;
    width: 24px;
    border-radius: var(--inistate-rounded-4);
    background-color: transparent;
    color: var(--inistate-color-neutral-500);
    transition: background-color 0.3s ease;
    > span {
      font-size: 16px;
    }
    &:hover {
      background-color: var(--inistate-color-neutral-100);
      cursor: pointer;
    }
  }
  .form-toggle-more-btn {
    flex-shrink: 0;
    height: 35px;
    min-width: 35px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
    border-radius: var(--inistate-rounded-4);
    color: var(--inistate-color-neutral-500);
    font-weight: var(--inistate-font-weight-medium);
    font-size: var(--inistate-paragraph-text-14);
    line-height: var(--inistate-paragraph-text-14--line-height);
    &:hover {
      background-color: var(--inistate-color-neutral-100);
      color: var(--inistate-text-secondary-color);
      cursor: pointer;
    }
    > .form-toggle-more-btn__icon {
      font-size: 16px;
      &:not(:last-child) {
        margin-right: 6px;
      }
    }
  }
}
</style>